import { ref, type Ref } from 'vue';
import { useSupplierStore } from '@/stores/supplier.store';
import { debounce } from 'lodash';

interface UseSupplierSelectOptions {
  customerId: Ref<any>;
  selectedId: Ref<any>;
  pageSize?: number;
  debounceMs?: number;
}

export function useSupplierSelect(options: UseSupplierSelectOptions) {
  const { customerId, selectedId, pageSize = 500, debounceMs = 300 } = options;
  const { getSuppliersList, getSupplierById } = useSupplierStore();

  const suppliers = ref<any[]>([]);
  const loading = ref(false);
  const hasMore = ref(false);
  const nextPage = ref<number | null>(null);
  const lastSearch = ref('');
  const selectedSupplierDeleted = ref(false);

  function addDisplayName(list: any[]) {
    list.forEach((s: any) => {
      s.display_name = `${s.name} - ${s.supplier_number}`;
    });
    return list;
  }

  async function ensureSelectedInList() {
    const id = selectedId.value;
    if (id == null) return;
    // Resolve object values like {id: 123, ...} to just the ID
    const numericId = typeof id === 'object' ? (id as any).id : id;
    if (numericId == null) return;
    const found = suppliers.value.some(
      (s: any) => s.id === numericId || String(s.id) === String(numericId)
    );
    if (found) {
      selectedSupplierDeleted.value = false;
      return;
    }
    try {
      const supplier = await getSupplierById(Number(numericId));
      if ((supplier as any).is_deleted) {
        selectedSupplierDeleted.value = true;
        (supplier as any).display_name = `${supplier.name} - ${supplier.supplier_number}`;
        suppliers.value = [supplier as any, ...suppliers.value];
      } else {
        selectedSupplierDeleted.value = false;
        (supplier as any).display_name = `${supplier.name} - ${supplier.supplier_number}`;
        suppliers.value = [supplier as any, ...suppliers.value];
      }
    } catch {
      // Supplier might have been hard-deleted or inaccessible
      selectedSupplierDeleted.value = true;
    }
  }

  async function fetchSuppliers(search?: string) {
    if (!customerId.value) return;
    loading.value = true;
    lastSearch.value = search || '';
    try {
      const response = await getSuppliersList({
        page_size: pageSize,
        customer: customerId.value,
        search: search || undefined,
      });
      suppliers.value = addDisplayName([...response.results]);
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
      if (!search) await ensureSelectedInList();
    } finally {
      loading.value = false;
    }
  }

  async function loadMore() {
    if (!nextPage.value || loading.value || !customerId.value) return;
    loading.value = true;
    try {
      const response = await getSuppliersList({
        page_size: pageSize,
        page: nextPage.value,
        customer: customerId.value,
        search: lastSearch.value || undefined,
      });
      const newResults = addDisplayName([...response.results]);
      suppliers.value = [...suppliers.value, ...newResults];
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
    } finally {
      loading.value = false;
    }
  }

  const onSearch = debounce((query: string) => {
    fetchSuppliers(query);
  }, debounceMs);

  return {
    suppliers,
    loading,
    hasMore,
    selectedSupplierDeleted,
    fetchSuppliers,
    loadMore,
    onSearch,
  };
}
